<template>
  <v-app>
    <v-container fluid>

      <v-row>

        <!-- LEFT SIDE -->
        <v-col md="3" cols="12">

          <v-expansion-panels
            v-model="panel"
            multiple
          >

            <v-expansion-panel>

              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Price Calculation Info
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- PRICE INQUIRY -->
                <v-autocomplete
                  label="Price Inquiry"
                  v-model="price_inquiry_master_id"
                  :items="getPriceInquiryList"
                  item-title="code"
                  item-value="id"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  clearable
                  :disabled="isEdit"
                  :error-messages="errors.price_inquiry_master_id"
                  @update:modelValue="loadInquiryDetails"
                />

                <!-- INQUIRY DETAILS -->
                <v-autocomplete
                  class="mt-3"
                  label="Inquiry Detail"
                  v-model="price_inquiry_details_id"
                  :items="inquiryDetails"
                  :item-title="(item) => `${item.product?.code || ''} - ${item.product?.name || 'N/A'}`"
                  item-value="id"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :disabled="isEdit"
                  clearable
                  :error-messages="errors.price_inquiry_details_id"
                  @update:modelValue="selectDetail"
                />

              </v-expansion-panel-text>

            </v-expansion-panel>

          </v-expansion-panels>

        </v-col>

        <!-- RIGHT SIDE -->
        <v-col md="9" cols="12">

          <v-expansion-panels
            v-model="panel1"
            multiple
          >

            <v-expansion-panel>

              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Price Calculation Form
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <v-row>

                  <!-- HS CODE -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.hs_code"
                      label="HS Code"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.hs_code"
                    />
                  </v-col>

                  <!-- ASSESSMENT VALUE -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.assessment_value"
                      label="Assessment Value"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.assessment_value"
                    />
                  </v-col>

                  <!-- TTI -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.tti"
                      label="TTI"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.tti"
                    />
                  </v-col>

                  <!-- SHIPPING COST -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.shipping_cost"
                      label="Shipping Cost"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.shipping_cost"
                    />
                  </v-col>

                  <!-- TAX PER KG -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.tax_per_kg"
                      label="Tax / KG"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.tax_per_kg"
                    />
                  </v-col>

                  <!-- TOTAL TAX -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.total_tax"
                      label="Total Tax"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.total_tax"
                    />
                  </v-col>

                  <!-- OTHER COST -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.other_cost"
                      label="Other Cost"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.other_cost"
                    />
                  </v-col>

                  <!-- PROFIT -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.profit"
                      label="Profit"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.profit"
                    />
                  </v-col>

                  <!-- PROPOSED PRICE -->
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="form.proposed_price"
                      label="Proposed Price"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      :error-messages="errors.proposed_price"
                    />
                  </v-col>

                </v-row>

                <!-- SAVE BUTTON -->
                <div class="mt-4 d-flex justify-end">

                  <v-btn
                    class="bg-primary"
                    :loading="loading"
                    @click="submitData"
                  >
                    {{ isEdit ? "Update" : "Post" }}
                  </v-btn>

                </div>

              </v-expansion-panel-text>

            </v-expansion-panel>

          </v-expansion-panels>

        </v-col>

      </v-row>

    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import * as yup from "yup";
import { useRouter, useRoute } from "vue-router";

const store = useStore();
const router = useRouter();
const route = useRoute();

/* ================= PANEL ================= */

const panel = ref([0]);
const panel1 = ref([0]);

/* ================= STATE ================= */

const loading = ref(false);

const isEdit = ref(false);
const priceCalculationId = ref(null);

const price_inquiry_master_id = ref(null);
const price_inquiry_details_id = ref(null);

const inquiryDetails = ref([]);
const selectedDetail = ref(null);

/* ================= ERRORS ================= */

const errors = ref({});

/* ================= FORM ================= */

const defaultForm = () => ({
  hs_code: "",

  assessment_value: 0,

  tti: 0,

  shipping_cost: 0,

  tax_per_kg: 0,

  total_tax: 0,

  other_cost: 0,

  profit: 0,

  proposed_price: 0,
});

const form = ref(defaultForm());

/* ================= VALIDATION ================= */

const schema = yup.object({

  price_inquiry_master_id: yup
    .number()
    .required("Price inquiry is required"),

  price_inquiry_details_id: yup
    .number()
    .required("Inquiry detail is required"),

  hs_code: yup
    .string()
    .required("HS code is required"),

  assessment_value: yup
    .number()
    .typeError("Assessment value must be number")
    .required("Assessment value is required")
    .min(0, "Minimum value 0"),

  tti: yup
    .number()
    .typeError("TTI must be number")
    .required("TTI is required")
    .min(0, "Minimum value 0"),

  shipping_cost: yup
    .number()
    .typeError("Shipping cost must be number")
    .required("Shipping cost is required")
    .min(0, "Minimum value 0"),

  tax_per_kg: yup
    .number()
    .typeError("Tax per KG must be number")
    .required("Tax per KG is required")
    .min(0, "Minimum value 0"),

  total_tax: yup
    .number()
    .typeError("Total tax must be number")
    .required("Total tax is required")
    .min(0, "Minimum value 0"),

  other_cost: yup
    .number()
    .typeError("Other cost must be number")
    .required("Other cost is required")
    .min(0, "Minimum value 0"),

  profit: yup
    .number()
    .typeError("Profit must be number")
    .required("Profit is required")
    .min(0, "Minimum value 0"),

  proposed_price: yup
    .number()
    .typeError("Proposed price must be number")
    .required("Proposed price is required")
    .min(0, "Minimum value 0"),

});

/* ================= GETTERS ================= */

const getPriceInquiryList = computed(() => {
  return store.getters.getPriceInquiryList || [];
});

/* ================= METHODS ================= */

const validate = async () => {

  errors.value = {};

  try {

    await schema.validate(
      {
        price_inquiry_master_id:
          price_inquiry_master_id.value,

        price_inquiry_details_id:
          price_inquiry_details_id.value,

        ...form.value,
      },
      {
        abortEarly: false,
      }
    );

    return true;

  } catch (error) {

    error.inner.forEach((err) => {
      errors.value[err.path] = err.message;
    });

    return false;

  }

};

const loadInquiryDetails = async () => {

  price_inquiry_details_id.value = null;

  selectedDetail.value = null;

  form.value = defaultForm();

  const inquiry = getPriceInquiryList.value.find(
    (x) => x.id == price_inquiry_master_id.value
  );

  inquiryDetails.value = inquiry?.details || [];

};

const selectDetail = () => {

  selectedDetail.value = inquiryDetails.value.find(
    (x) => x.id == price_inquiry_details_id.value
  );

  if (!selectedDetail.value) {

    form.value = defaultForm();

    return;

  }

  form.value = {

    hs_code:
      selectedDetail.value.priceCalculation?.hs_code || "",

    assessment_value:
      selectedDetail.value.priceCalculation?.assessment_value || 0,

    tti:
      selectedDetail.value.priceCalculation?.tti || 0,

    shipping_cost:
      selectedDetail.value.priceCalculation?.shipping_cost || 0,

    tax_per_kg:
      selectedDetail.value.priceCalculation?.tax_per_kg || 0,

    total_tax:
      selectedDetail.value.priceCalculation?.total_tax || 0,

    other_cost:
      selectedDetail.value.priceCalculation?.other_cost || 0,

    profit:
      selectedDetail.value.priceCalculation?.profit || 0,

    proposed_price:
      selectedDetail.value.priceCalculation?.proposed_price || 0,

  };

};

const loadPriceCalculation = async (id) => {

  try {

    const res = await store.dispatch(
      "getPriceCalculationById",
      id
    );

    const data = res.data;

    store.commit('SET_PRICE_INQUIRY_LIST', [data.inquiry_master]);
    inquiryDetails.value = [data.inquiry_detail];
    console.log("UUUUUUUUUU");
    console.log(inquiryDetails.value);
    // inquiryMaster

    price_inquiry_master_id.value =
      data.price_inquiry_master_id;

    // await loadInquiryDetails();

    price_inquiry_details_id.value =
      data.price_inquiry_details_id;

    form.value = {

      hs_code: data.hs_code || "",

      assessment_value:
        data.assessment_value || 0,

      tti:
        data.tti || 0,

      shipping_cost:
        data.shipping_cost || 0,

      tax_per_kg:
        data.tax_per_kg || 0,

      total_tax:
        data.total_tax || 0,

      other_cost:
        data.other_cost || 0,

      profit:
        data.profit || 0,

      proposed_price:
        data.proposed_price || 0,

    };

  } catch (error) {

    toast.error("Failed to load price calculation");

  }

};

const submitData = async () => {

  const isValid = await validate();

  if (!isValid) return;

  loading.value = true;

  try {

    const payload = {

      ...(isEdit.value && {
        price_calculation_details_id:
          priceCalculationId.value,
      }),

      price_inquiry_master_id:
        price_inquiry_master_id.value,

      price_inquiry_details_id:
        price_inquiry_details_id.value,

      ...form.value,

    };

    await store.dispatch(
      "saveUpdatePriceCalculation",
      payload
    );

    toast.success(
      isEdit.value
        ? "Updated successfully"
        : "Saved successfully"
    );

    router.push({
      name: "price-calculation-records",
    });

  } catch (error) {

    toast.error(
      error.response?.data?.message ||
      "Something went wrong"
    );

  } finally {

    loading.value = false;

  }

};

/* ================= MOUNTED ================= */

onMounted(async () => {

  loading.value = true;

  try {

    await store.dispatch(
      "getPriceInquiries",
      {
        type: "inquiry_for_calculation",
      }
    );

    if (route.params.id) {

      isEdit.value = true;

      priceCalculationId.value =
        route.params.id;

      await loadPriceCalculation(
        priceCalculationId.value
      );

    }

  } finally {

    loading.value = false;

  }

});
</script>